/**
 * SiteModePanel - Site Mode menu panel for roof management
 *
 * Handles roof selection, editing, obstructions, and polygon management.
 */

import React from 'react';
import {
  Home as HomeIcon,
  ChevronDown,
  Square,
  Plus,
  Edit3,
  Trash2,
  PenTool,
  X,
  AlertTriangle,
  Circle,
  Pentagon,
} from 'lucide-react';
import { RoofList } from './RoofList';

interface RoofData {
  id: number;
  area: number;
  moduleCount: number;
}

interface SiteModePanelProps {
  // Collapse states
  roofCardCollapsed: boolean;
  setRoofCardCollapsed: (v: boolean) => void;
  obstructionCardCollapsed: boolean;
  setObstructionCardCollapsed: (v: boolean) => void;

  // Display toggles
  showMeasurements: boolean;
  setShowMeasurements: (v: boolean) => void;
  showAllPolygons: boolean;
  setShowAllPolygons: (v: boolean) => void;

  // Roof list data
  roofs: RoofData[];
  activeRoofId: number | null;
  selectedPolygon: number | null;
  onSelectRoof: (roofId: number) => void;
  onRemoveRoof: (roofId: number) => void;

  // Roof select mode
  isRoofSelectMode: boolean;
  setIsRoofSelectMode: (v: boolean) => void;

  // Polygon info
  calculatePolygonArea: (coords: [number, number][]) => number;
  getPolygonCoords: (polygonId: number) => [number, number][];
  isRoofEstablished: (polygonId: number | null) => boolean;

  // Establish/Convert roof
  onConvertToRoof: (polygonId: number) => Promise<void>;

  // Polygon editing
  editingPolygonVertex: number | null;
  setEditingPolygonVertex: (v: number | null) => void;
  onEditPolygon: (polygonId: number) => void;
  onDeletePolygon: (polygonId: number) => void;

  // Edge & Azimuth
  snapEdgeIndex: Record<number, number>;
  calculateEdgeAzimuth: (polygonId: number) => number | null;
  isSelectingSnapEdge: boolean;
  setIsSelectingSnapEdge: (v: boolean) => void;

  // Draw new roof
  isDrawingRoof: boolean;
  onStartDrawingRoof: () => void;
  onResetPolygons: () => void;

  // Obstructions
  obstructions: Record<number, [number, number][][]>;
  isDrawingObstruction: boolean;
  setIsDrawingObstruction: (v: boolean) => void;
  obstructionDrawMode: 'rectangle' | 'circle' | 'polygon';
  setObstructionDrawMode: (mode: 'rectangle' | 'circle' | 'polygon') => void;
  selectedObstruction: { polygonId: number; index: number } | null;
  setSelectedObstruction: (v: { polygonId: number; index: number } | null) => void;
  onDeleteObstruction: (polygonId: number, index: number) => void;
}

export function SiteModePanel({
  roofCardCollapsed,
  setRoofCardCollapsed,
  obstructionCardCollapsed,
  setObstructionCardCollapsed,
  showMeasurements,
  setShowMeasurements,
  showAllPolygons,
  setShowAllPolygons,
  roofs,
  activeRoofId,
  selectedPolygon,
  onSelectRoof,
  onRemoveRoof,
  isRoofSelectMode,
  setIsRoofSelectMode,
  calculatePolygonArea,
  getPolygonCoords,
  isRoofEstablished,
  onConvertToRoof,
  editingPolygonVertex,
  setEditingPolygonVertex,
  onEditPolygon,
  onDeletePolygon,
  snapEdgeIndex,
  calculateEdgeAzimuth,
  isSelectingSnapEdge,
  setIsSelectingSnapEdge,
  isDrawingRoof,
  onStartDrawingRoof,
  onResetPolygons,
  obstructions,
  isDrawingObstruction,
  setIsDrawingObstruction,
  obstructionDrawMode,
  setObstructionDrawMode,
  selectedObstruction,
  setSelectedObstruction,
  onDeleteObstruction,
}: SiteModePanelProps) {
  return (
    <div className="space-y-1">
      {/* Roof Menu Item */}
      <div className="border-b border-gray-200">
        <button
          onClick={() => setRoofCardCollapsed(!roofCardCollapsed)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <HomeIcon className="h-5 w-5 text-gray-700" />
            <span className="text-sm font-semibold text-gray-900">Roof</span>
          </div>
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${!roofCardCollapsed ? 'rotate-180' : ''}`} />
        </button>

        {/* Roof Controls - Expanded inline */}
        {!roofCardCollapsed && (
          <div className="p-4 space-y-4">
            {/* Show Measurements Toggle */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Show Measurements</span>
              <button
                onClick={() => setShowMeasurements(!showMeasurements)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showMeasurements ? 'bg-[#EBFD03]' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  showMeasurements ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {/* Show Other Polygons Toggle */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Show Other Polygons</span>
              <button
                onClick={() => setShowAllPolygons(!showAllPolygons)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showAllPolygons ? 'bg-[#EBFD03]' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  showAllPolygons ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {/* Roof List */}
            <RoofList
              roofs={roofs}
              activeRoofId={activeRoofId}
              selectedPolygon={selectedPolygon}
              onSelectRoof={onSelectRoof}
              onRemoveRoof={onRemoveRoof}
            />

            {/* Add Roof Mode Buttons */}
            <div className="pt-4 border-t-2 border-gray-200 space-y-2">
              {isRoofSelectMode ? (
                <button
                  onClick={() => setIsRoofSelectMode(false)}
                  className="w-full bg-[#EBFD03] text-black font-semibold py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
                >
                  <Square className="h-4 w-4" />
                  <span>Exit Select Mode</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    setIsRoofSelectMode(true);
                    setShowAllPolygons(true);
                  }}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Roof</span>
                </button>
              )}

              {isRoofSelectMode && (
                <div className="bg-blue-100 border-2 border-blue-400 rounded-lg p-2 text-xs text-blue-800">
                  Click any polygon on the map to add it to your roof list
                </div>
              )}
            </div>

            {/* Selected Polygon Info */}
            <div>
              <label className="text-xs text-gray-600 mb-2 block font-semibold">Selected Polygon</label>
              {selectedPolygon === null ? (
                <div className="text-sm text-gray-600 italic bg-gray-50 rounded-lg p-3 border-2 border-gray-200">
                  Click a polygon on the map to select
                </div>
              ) : (
                <div className="bg-white border-2 border-gray-300 rounded-lg p-3 space-y-2">
                  <div className="text-sm font-semibold text-black">Polygon #{selectedPolygon}</div>
                  {calculatePolygonArea(getPolygonCoords(selectedPolygon)) > 0 && (
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="font-semibold">Gross Area:</span>
                        <span>{calculatePolygonArea(getPolygonCoords(selectedPolygon)).toFixed(2)} m²</span>
                      </div>
                    </div>
                  )}
                  {!isRoofEstablished(selectedPolygon) && (
                    <button
                      onClick={() => onConvertToRoof(selectedPolygon)}
                      className="w-full bg-[#EBFD03] hover:bg-[#d4e003] text-black font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      Convert to Roof
                    </button>
                  )}
                  {isRoofEstablished(selectedPolygon) && (
                    <div className="bg-green-50 border-2 border-green-500 rounded-lg p-2">
                      <div className="flex items-center gap-2 text-green-700 text-sm font-semibold">
                        <HomeIcon className="h-4 w-4" />
                        <span>Roof Established</span>
                      </div>
                      <p className="text-xs text-gray-700 mt-1">This polygon is now your active roof</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Edit Controls - Only show when polygon selected */}
            {selectedPolygon !== null && (
              <div className="space-y-2">
                {isRoofSelectMode && (
                  <button
                    onClick={() => setIsRoofSelectMode(false)}
                    className="w-full bg-[#EBFD03] text-black font-semibold py-2 px-4 rounded-lg flex items-center justify-center gap-2"
                  >
                    <Square className="h-4 w-4" />
                    <span>Exit Select Mode</span>
                  </button>
                )}
                {!isRoofSelectMode && (
                  <button
                    onClick={() => {
                      setIsRoofSelectMode(true);
                      setShowAllPolygons(true);
                    }}
                    className="w-full bg-gray-200 hover:bg-gray-300 text-black font-semibold py-2 px-4 rounded-lg flex items-center justify-center gap-2"
                  >
                    <Square className="h-4 w-4" />
                    <span>Select Roof</span>
                  </button>
                )}

                <button
                  onClick={() => onEditPolygon(selectedPolygon)}
                  className="w-full bg-gray-700 hover:bg-gray-800 text-white font-semibold py-2 px-4 rounded-lg flex items-center justify-center gap-2"
                >
                  <Edit3 className="h-4 w-4" />
                  <span>Edit Polygon</span>
                </button>

                <button
                  onClick={() => onDeletePolygon(selectedPolygon)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg flex items-center justify-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Delete Polygon</span>
                </button>
              </div>
            )}

            {/* Edge & Azimuth - Only show when polygon selected and not editing */}
            {selectedPolygon !== null && editingPolygonVertex === null && (
              <div className="pt-4 border-t-2 border-gray-200">
                <label className="text-xs text-gray-600 mb-2 block font-semibold">EDGE & AZIMUTH</label>
                {snapEdgeIndex[selectedPolygon] !== undefined && (
                  <div className="bg-green-50 border-2 border-green-500 rounded-lg p-3 mb-2">
                    <div className="text-sm font-semibold text-green-700">Edge {snapEdgeIndex[selectedPolygon]} selected</div>
                    <div className="text-xs text-gray-700">Azimuth: {calculateEdgeAzimuth(selectedPolygon)?.toFixed(0)}° SSE</div>
                  </div>
                )}
                <button
                  onClick={() => setIsSelectingSnapEdge(!isSelectingSnapEdge)}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-black font-semibold py-2 px-4 rounded-lg"
                >
                  {isSelectingSnapEdge ? 'Cancel' : 'Select Edge'}
                </button>
              </div>
            )}

            {/* Draw New Roof & Reset */}
            {editingPolygonVertex === null && (
              <div className="pt-4 border-t-2 border-gray-200 space-y-2">
                <button
                  onClick={onStartDrawingRoof}
                  className={`w-full font-semibold py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-all ${
                    isDrawingRoof
                      ? 'bg-blue-500 text-white border-2 border-blue-300 animate-pulse'
                      : 'bg-gray-200 hover:bg-gray-300 text-black'
                  }`}
                >
                  <PenTool className="h-4 w-4" />
                  <span>{isDrawingRoof ? 'Drawing Mode Active...' : 'Draw New Roof'}</span>
                </button>

                {/* Drawing Instructions */}
                {isDrawingRoof && (
                  <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-3 text-xs space-y-1">
                    <div className="font-semibold text-blue-900">Drawing Instructions:</div>
                    <div className="text-blue-800">• Click to add vertices</div>
                    <div className="text-blue-800">• Double-click to finish</div>
                    <div className="text-blue-800">• Hold <kbd className="px-1 py-0.5 bg-white border border-blue-300 rounded text-xs font-mono">Shift</kbd> for orthogonal angles</div>
                  </div>
                )}

                <button
                  onClick={onResetPolygons}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-black font-semibold py-2 px-4 rounded-lg flex items-center justify-center gap-2"
                >
                  <X className="h-4 w-4" />
                  <span>Reset Polygons</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Obstructions Menu Item */}
      <div className="border-b border-gray-200">
        <button
          onClick={() => setObstructionCardCollapsed(!obstructionCardCollapsed)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-gray-700" />
            <span className="text-sm font-semibold text-gray-900">Obstructions</span>
          </div>
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${!obstructionCardCollapsed ? 'rotate-180' : ''}`} />
        </button>

        {/* Obstruction Controls */}
        {!obstructionCardCollapsed && (
          <div className="p-4 space-y-4">
            {/* Global obstruction indicator when no polygon selected */}
            {selectedPolygon === null && (
              <div className="text-xs text-blue-600 bg-blue-50 rounded-lg p-2 border border-blue-200">
                Drawing global obstructions (no roof selected)
              </div>
            )}

            {/* Drawing Controls - always available */}
            {!isDrawingObstruction ? (
              <>
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-700">Draw Mode</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => {
                        setObstructionDrawMode('rectangle');
                        setIsDrawingObstruction(true);
                      }}
                      className="flex flex-col items-center justify-center gap-1 p-2 rounded-lg border-2 border-gray-300 hover:border-[#EBFD03] hover:bg-[#EBFD03]/10 transition-colors"
                    >
                      <Square className="h-5 w-5 text-gray-700" />
                      <span className="text-xs font-semibold text-gray-700">Rectangle</span>
                    </button>
                    <button
                      onClick={() => {
                        setObstructionDrawMode('circle');
                        setIsDrawingObstruction(true);
                      }}
                      className="flex flex-col items-center justify-center gap-1 p-2 rounded-lg border-2 border-gray-300 hover:border-[#EBFD03] hover:bg-[#EBFD03]/10 transition-colors"
                    >
                      <Circle className="h-5 w-5 text-gray-700" />
                      <span className="text-xs font-semibold text-gray-700">Circle</span>
                    </button>
                    <button
                      onClick={() => {
                        setObstructionDrawMode('polygon');
                        setIsDrawingObstruction(true);
                      }}
                      className="flex flex-col items-center justify-center gap-1 p-2 rounded-lg border-2 border-gray-300 hover:border-[#EBFD03] hover:bg-[#EBFD03]/10 transition-colors"
                    >
                      <Pentagon className="h-5 w-5 text-gray-700" />
                      <span className="text-xs font-semibold text-gray-700">Polygon</span>
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-600">
                  Choose a shape and draw on the map (chimneys, vents, skylights, etc.)
                </p>
              </>
            ) : (
              <>
                <div className="bg-red-50 border-2 border-red-500 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-700 text-sm font-semibold mb-1">
                    {obstructionDrawMode === 'rectangle' && <Square className="h-4 w-4" />}
                    {obstructionDrawMode === 'circle' && <Circle className="h-4 w-4" />}
                    {obstructionDrawMode === 'polygon' && <Pentagon className="h-4 w-4" />}
                    <span>Drawing {obstructionDrawMode.charAt(0).toUpperCase() + obstructionDrawMode.slice(1)}</span>
                  </div>
                  <p className="text-xs text-gray-700">
                    {obstructionDrawMode === 'rectangle' && 'Click and drag to draw a rectangle'}
                    {obstructionDrawMode === 'circle' && 'Click center, then drag to set radius'}
                    {obstructionDrawMode === 'polygon' && 'Click to place vertices, double-click to finish'}
                  </p>
                </div>
                <button
                  onClick={() => setIsDrawingObstruction(false)}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-black font-semibold py-2 px-4 rounded-lg"
                >
                  Done Adding
                </button>
              </>
            )}

            {/* Obstruction List - show for selected polygon or global obstructions */}
            {(() => {
              const targetPolygonId = selectedPolygon !== null ? selectedPolygon : -1; // -1 is FREE_FIELD_POLYGON_ID
              const obstructionList = obstructions[targetPolygonId] || [];
              return obstructionList.length > 0 && !isDrawingObstruction && (
                <div className="pt-4 border-t-2 border-gray-200">
                  <h4 className="text-xs font-semibold text-gray-600 mb-2">
                    {selectedPolygon === null ? 'Global ' : ''}Obstructions ({obstructionList.length})
                  </h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {obstructionList.map((_, index) => {
                      const isSelected = selectedObstruction?.polygonId === targetPolygonId && selectedObstruction?.index === index;
                      return (
                        <div
                          key={index}
                          className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-[#EBFD03]/20 border-2 border-[#EBFD03]'
                              : 'bg-gray-50 border-2 border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => {
                            setSelectedObstruction({ polygonId: targetPolygonId, index });
                          }}
                        >
                          <span className="text-sm text-gray-700">Obstruction {index + 1}</span>
                          {isSelected && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteObstruction(targetPolygonId, index);
                              }}
                              className="text-red-600 hover:text-red-800 transition-colors"
                              title="Delete obstruction"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

export default SiteModePanel;
